import React, { memo, useMemo, useState, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Button,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  AddBox as AddBoxIcon,
  ExpandMore as ExpandMoreIcon,
  DeleteOutline as DeleteIcon,
  DriveFileMove as MoveIcon,
  CheckBox as CheckedIcon,
} from '@mui/icons-material';
import { sortedTickers } from '../utils/listEdits';
import { isExcluded } from '../utils/typeFilter';

const PANEL_WIDTH = 260;
const COLLAPSED_WIDTH = 30;

/**
 * Explorer-style side panel listing the user's ticker lists.
 *
 * - Click a list: load it into the active matrix (the highlighted one). The +
 *   button opens it in a new matrix.
 * - Ctrl/Cmd+click toggles a list in the selection and Shift+click selects a
 *   range, as in a file manager; the selection feeds "Compare sectors".
 * - Drag a list onto another to merge them (after a confirmation).
 * - The chevron expands a list to its tickers, A-Z. Click selects (Ctrl/Shift for
 *   several); drag them onto another list to move them (hold Alt to copy), or use
 *   Move / Delete (or the Delete key) on the selection.
 * - The search box matches list names and, failing that, tickers inside a list, so
 *   "which list has BAC PRL" is one keystroke away. Enter opens the first match,
 *   Escape clears.
 *
 * `lists` are the STORED lists. Tickers hidden by a type filter stay listed here
 * (struck through, with the reason) because this panel is where lists are edited.
 */
const ListsPanel = ({
  lists,
  defaultListNames = [],  // lists that started as GICS sector defaults
  open,
  onToggle,
  boxes,          // [{ id, listName }] in display order
  activeBoxId,
  onSelectList,   // (listName) => void — load into the active matrix
  onOpenInNewBox, // (listName) => void
  canAddBox,
  topOffset = 0,
  selectedLists = [],            // lists picked for "Compare sectors"
  onSelectedListsChange = () => {},
  editing,                       // from useListEditing
  tickerTypes = null,            // TICKER -> 'c'|'f'|'v'... (for the type-filter marks)
  excludedFlags = '',            // flags currently excluded, e.g. 'c'
}) => {
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [expanded, setExpanded] = useState({});
  const [tickerSel, setTickerSel] = useState({ list: null, set: new Set() });
  const [dropTarget, setDropTarget] = useState(null);
  const inputRef = useRef(null);
  const dragRef = useRef(null);       // { kind: 'list'|'tickers', list, tickers? }
  const listAnchor = useRef(null);
  const tickerAnchor = useRef(null);
  const sectorSet = useMemo(() => new Set(defaultListNames), [defaultListNames]);
  const selectedSet = useMemo(() => new Set(selectedLists), [selectedLists]);

  // listName -> display positions (1-based) of the matrices showing it
  const openIn = useMemo(() => {
    const out = {};
    boxes.forEach((b, i) => {
      if (!b.listName) return;
      (out[b.listName] = out[b.listName] || []).push({ pos: i + 1, active: b.id === activeBoxId });
    });
    return out;
  }, [boxes, activeBoxId]);

  const items = useMemo(() => {
    const q = query.trim().toUpperCase();
    const names = Object.keys(lists || {}).sort((a, b) => a.localeCompare(b));
    if (!q) return names.map(name => ({ name, match: null }));
    const out = [];
    for (const name of names) {
      if (name.toUpperCase().includes(q)) { out.push({ name, match: null }); continue; }
      const hits = (lists[name] || []).filter(t => t.toUpperCase().includes(q));
      if (hits.length) out.push({ name, match: hits });
    }
    return out;
  }, [lists, query]);

  // Own lists first, then the GICS sector defaults. A search expands both.
  const sections = useMemo(() => [
    { key: 'mine', title: 'My lists', items: items.filter(i => !sectorSet.has(i.name)) },
    {
      key: 'sector',
      title: 'Sector lists · GICS',
      hint: 'One list per GICS sub-industry, from the tickers with full price and dividend data. ' +
        'A list you have not edited stays up to date; once you edit it, it is yours and no longer changes.',
      items: items.filter(i => sectorSet.has(i.name)),
    },
  ], [items, sectorSet]);
  const isCollapsed = (key) => !query && !!collapsed[key];

  // The order rows appear in, for Shift+click ranges.
  const visibleNames = useMemo(
    () => sections.flatMap(s => (isCollapsed(s.key) ? [] : s.items.map(i => i.name))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections, collapsed, query]
  );

  // ---- list selection -------------------------------------------------------
  const handleListClick = (e, name) => {
    if (e.shiftKey && listAnchor.current && visibleNames.includes(listAnchor.current)) {
      const a = visibleNames.indexOf(listAnchor.current);
      const b = visibleNames.indexOf(name);
      const [lo, hi] = a <= b ? [a, b] : [b, a];
      const range = visibleNames.slice(lo, hi + 1);
      // Ctrl+Shift adds the range to what is selected; Shift alone replaces it.
      onSelectedListsChange((e.ctrlKey || e.metaKey) ? [...new Set([...selectedLists, ...range])] : range);
      return;
    }
    if (e.ctrlKey || e.metaKey) {
      listAnchor.current = name;
      onSelectedListsChange(selectedSet.has(name) ? selectedLists.filter(n => n !== name) : [...selectedLists, name]);
      return;
    }
    listAnchor.current = name;
    if (selectedLists.length) onSelectedListsChange([]);
    onSelectList(name);
  };

  // ---- ticker selection -----------------------------------------------------
  const handleTickerClick = (e, list, ticker, order) => {
    e.stopPropagation();
    setTickerSel((cur) => {
      const base = cur.list === list ? cur.set : new Set();
      if (e.shiftKey && tickerAnchor.current && tickerAnchor.current.list === list) {
        const a = order.indexOf(tickerAnchor.current.ticker);
        const b = order.indexOf(ticker);
        const [lo, hi] = a <= b ? [a, b] : [b, a];
        const range = order.slice(lo, hi + 1);
        return { list, set: new Set((e.ctrlKey || e.metaKey) ? [...base, ...range] : range) };
      }
      tickerAnchor.current = { list, ticker };
      if (e.ctrlKey || e.metaKey) {
        const next = new Set(base);
        if (next.has(ticker)) next.delete(ticker); else next.add(ticker);
        return { list, set: next };
      }
      return { list, set: new Set([ticker]) };
    });
  };

  const selectedTickers = (list) => (tickerSel.list === list ? [...tickerSel.set] : []);
  const clearTickerSel = () => setTickerSel({ list: null, set: new Set() });

  const deleteSelected = useCallback((list) => {
    const ts = selectedTickers(list);
    if (!ts.length) return;
    editing.requestDeleteTickers(list, ts);
    clearTickerSel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickerSel, editing]);

  const moveSelected = (list) => {
    const ts = selectedTickers(list);
    if (ts.length) editing.requestMoveTickers(list, ts);
  };

  // ---- drag and drop --------------------------------------------------------
  const startListDrag = (e, name) => {
    dragRef.current = { kind: 'list', list: name };
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', name);
  };

  const startTickerDrag = (e, list, ticker) => {
    const already = tickerSel.list === list && tickerSel.set.has(ticker);
    const tickers = already ? [...tickerSel.set] : [ticker];
    if (!already) setTickerSel({ list, set: new Set([ticker]) });
    dragRef.current = { kind: 'tickers', list, tickers };
    e.dataTransfer.effectAllowed = 'copyMove';
    e.dataTransfer.setData('text/plain', tickers.join(', '));
  };

  const endDrag = () => { dragRef.current = null; setDropTarget(null); };

  const canDropOn = (name) => {
    const d = dragRef.current;
    return !!d && name !== d.list;
  };

  const handleDragOver = (e, name) => {
    if (!canDropOn(name)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = dragRef.current.kind === 'tickers' && e.altKey ? 'copy' : 'move';
    if (dropTarget !== name) setDropTarget(name);
  };

  const handleDrop = (e, name) => {
    const d = dragRef.current;
    if (!d || name === d.list) { endDrag(); return; }
    e.preventDefault();
    endDrag();
    if (d.kind === 'list') editing.requestMerge(d.list, name);
    else editing.applyMove(d.list, name, d.tickers, e.altKey);
  };

  // ---- rendering ------------------------------------------------------------
  const renderTickers = (name) => {
    const order = sortedTickers(lists[name]);
    const sel = tickerSel.list === name ? tickerSel.set : null;
    return (
      <Box
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Delete' || e.key === 'Backspace') && sel && sel.size) { e.preventDefault(); deleteSelected(name); }
          if (e.key === 'Escape') clearTickerSel();
        }}
        sx={{ outline: 'none', pl: 2.25, pr: 0.5, pb: 0.5, borderLeft: `2px solid ${theme.palette.divider}`, ml: 1.5 }}
      >
        {sel && sel.size > 0 && (
          <Box display="flex" alignItems="center" sx={{ py: 0.25, gap: 0.5 }}>
            <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>{sel.size} selected</Typography>
            <Tooltip title="Move or copy to another list">
              <IconButton size="small" onClick={() => moveSelected(name)} aria-label="Move selected tickers"><MoveIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
            <Tooltip title="Remove from this list (Delete)">
              <IconButton size="small" color="error" onClick={() => deleteSelected(name)} aria-label="Delete selected tickers"><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
          </Box>
        )}
        {order.map((t) => {
          const isSel = !!sel && sel.has(t);
          const hidden = isExcluded(t, tickerTypes, excludedFlags);
          return (
            <Box
              key={t}
              draggable
              onDragStart={(e) => startTickerDrag(e, name, t)}
              onDragEnd={endDrag}
              onClick={(e) => handleTickerClick(e, name, t, order)}
              title={hidden ? `${t} is hidden from the matrices by a type filter (it is still in this list)` : `${t} — drag onto a list to move it (Alt: copy)`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 0.75,
                py: 0.125,
                fontSize: '0.74rem',
                cursor: 'grab',
                userSelect: 'none',
                borderRadius: 0.5,
                color: hidden ? 'text.disabled' : 'text.primary',
                textDecoration: hidden ? 'line-through' : 'none',
                backgroundColor: isSel ? alpha(theme.palette.primary.main, 0.22) : 'transparent',
                '&:hover': { backgroundColor: isSel ? alpha(theme.palette.primary.main, 0.28) : theme.palette.action.hover },
              }}
            >
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t}</span>
              {hidden && <span style={{ fontSize: '0.62rem', opacity: 0.8 }}>{tickerTypes[t]}</span>}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderItem = ({ name, match }) => {
    const where = openIn[name] || [];
    const inActive = where.some(w => w.active);
    const isSelected = selectedSet.has(name);
    const isDrop = dropTarget === name;
    const isOpen = !!expanded[name] || (!!query && !!match);
    const stored = lists[name] || [];
    return (
      <React.Fragment key={name}>
        <ListItemButton
          selected={inActive && !isSelected}
          draggable
          onDragStart={(e) => startListDrag(e, name)}
          onDragEnd={endDrag}
          onDragOver={(e) => handleDragOver(e, name)}
          onDragLeave={() => dropTarget === name && setDropTarget(null)}
          onDrop={(e) => handleDrop(e, name)}
          onClick={(e) => handleListClick(e, name)}
          title={`Open ${name} in the active matrix · Ctrl/Shift+click to select several · drag onto another list to merge`}
          sx={{
            py: 0.25,
            pl: 0.25,
            pr: 0.5,
            borderLeft: `3px solid ${inActive ? theme.palette.primary.main : 'transparent'}`,
            outline: isDrop ? `2px dashed ${theme.palette.warning.main}` : 'none',
            outlineOffset: -2,
            backgroundColor: isSelected ? alpha(theme.palette.secondary.main, 0.28) : undefined,
            '&:hover': isSelected ? { backgroundColor: alpha(theme.palette.secondary.main, 0.36) } : undefined,
            '& .open-new': { visibility: 'hidden' },
            '&:hover .open-new': { visibility: 'visible' },
            '&.Mui-selected': { backgroundColor: alpha(theme.palette.primary.main, 0.14) },
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setExpanded(x => ({ ...x, [name]: !isOpen })); }}
            aria-label={isOpen ? `Hide tickers of ${name}` : `Show tickers of ${name}`}
            aria-expanded={isOpen}
            sx={{ p: 0.125, mr: 0.25 }}
          >
            {isOpen ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
          </IconButton>
          {isSelected && <CheckedIcon sx={{ fontSize: 14, mr: 0.5, color: 'secondary.main' }} />}
          <ListItemText
            primary={name}
            secondary={match ? `contains ${match.slice(0, 3).join(', ')}${match.length > 3 ? '…' : ''}` : null}
            primaryTypographyProps={{ fontSize: '0.8rem', noWrap: true, fontWeight: where.length || isSelected ? 600 : 400 }}
            secondaryTypographyProps={{ fontSize: '0.68rem', noWrap: true }}
            sx={{ my: 0 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, flexShrink: 0 }}>
            {stored.length}
          </Typography>
          {where.map(w => (
            <Box
              key={w.pos}
              component="span"
              title={`Open in matrix #${w.pos}`}
              sx={{
                ml: 0.5,
                px: 0.5,
                borderRadius: 0.5,
                fontSize: '0.65rem',
                fontWeight: 700,
                lineHeight: 1.5,
                flexShrink: 0,
                color: w.active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
                backgroundColor: w.active ? theme.palette.primary.main : theme.palette.action.selected,
              }}
            >
              #{w.pos}
            </Box>
          ))}
          <Tooltip title={canAddBox ? 'Open in a new matrix' : 'Maximum matrices reached'} placement="right">
            <span className="open-new">
              <IconButton
                size="small"
                disabled={!canAddBox}
                onClick={(e) => { e.stopPropagation(); onOpenInNewBox(name); }}
                aria-label={`Open ${name} in a new matrix`}
                sx={{ p: 0.25, ml: 0.25 }}
              >
                <AddBoxIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </ListItemButton>
        {isOpen && renderTickers(name)}
      </React.Fragment>
    );
  };

  const stickySx = {
    position: 'sticky',
    top: topOffset,
    height: `calc(100vh - ${topOffset + 16}px)`,
    flexShrink: 0,
  };

  if (!open) {
    return (
      <Paper
        elevation={2}
        sx={{
          ...stickySx,
          width: COLLAPSED_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 0.5,
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        <Tooltip title="Show lists" placement="right">
          <IconButton size="small" aria-label="Show lists panel">
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{ writingMode: 'vertical-rl', mt: 1, color: 'text.secondary', letterSpacing: 1, fontWeight: 600 }}
        >
          LISTS
        </Typography>
      </Paper>
    );
  }

  const listCount = Object.keys(lists || {}).length;

  return (
    <Paper
      elevation={2}
      sx={{ ...stickySx, width: PANEL_WIDTH, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <Box display="flex" alignItems="center" sx={{ px: 1, pt: 0.75, pb: 0.5 }}>
        <Typography variant="overline" sx={{ flex: 1, fontWeight: 700, lineHeight: 1.6, color: 'text.secondary' }}>
          Lists ({listCount})
        </Typography>
        <Tooltip title="Hide lists" placement="right">
          <IconButton size="small" onClick={onToggle} aria-label="Hide lists panel">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          mx: 1,
          mb: 0.5,
          px: 0.75,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          '&:focus-within': { borderColor: theme.palette.primary.main },
        }}
      >
        <SearchIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && items.length > 0) onSelectList(items[0].name);
            if (e.key === 'Escape') setQuery('');
          }}
          placeholder="Search lists or tickers"
          inputProps={{ 'aria-label': 'Search lists or tickers' }}
          sx={{ flex: 1, fontSize: '0.8rem', py: 0.25 }}
        />
        {query && (
          <IconButton size="small" onClick={() => { setQuery(''); inputRef.current?.focus(); }} aria-label="Clear search" sx={{ p: 0.25 }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>

      {selectedLists.length > 0 && (
        <Box
          display="flex"
          alignItems="center"
          sx={{ mx: 1, mb: 0.5, px: 1, py: 0.25, borderRadius: 1, backgroundColor: alpha(theme.palette.secondary.main, 0.18) }}
        >
          <Typography variant="caption" sx={{ flex: 1, fontWeight: 700 }}>
            {selectedLists.length} selected for comparison
          </Typography>
          <Button size="small" onClick={() => onSelectedListsChange([])} sx={{ minWidth: 0, py: 0, fontSize: '0.7rem' }}>
            Clear
          </Button>
        </Box>
      )}

      <List dense disablePadding sx={{ flex: 1, overflowY: 'auto', pb: 1 }}>
        {items.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, py: 1 }}>
            {listCount === 0 ? 'No lists yet — upload one from the menu.' : 'No list matches.'}
          </Typography>
        )}
        {sections.map(section => section.items.length > 0 && (
          <React.Fragment key={section.key}>
            <ListItemButton
              onClick={() => setCollapsed(c => ({ ...c, [section.key]: !c[section.key] }))}
              sx={{ py: 0.25, px: 0.75, minHeight: 0 }}
              aria-expanded={!isCollapsed(section.key)}
              title={section.hint}
            >
              {isCollapsed(section.key)
                ? <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
              <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5 }}>
                {section.title} ({section.items.length})
              </Typography>
            </ListItemButton>
            {!isCollapsed(section.key) && section.items.map(renderItem)}
          </React.Fragment>
        ))}
      </List>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ px: 1, py: 0.5, borderTop: `1px solid ${theme.palette.divider}`, fontSize: '0.64rem', lineHeight: 1.3 }}
      >
        Click opens · Ctrl/Shift+click selects several · drag a list onto another to merge · ▸ shows tickers: drag to move (Alt copies)
      </Typography>
    </Paper>
  );
};

export default memo(ListsPanel);
